/**
 * Production API Implementation of IFacilityRepository
 */
import api from '../../../lib/api';
import { Facility, Slot, FairnessProfile, FacilityBooking, CreateBookingDTO } from '../types/facility';
import { IFacilityRepository } from './IFacilityRepository';

export class ApiFacilityRepository implements IFacilityRepository {
  async getFacilities(societyId: number, signal?: AbortSignal): Promise<Facility[]> {
    const res = await api.get(`/societies/${societyId}/facilities`, { signal });
    return res.data?.data || res.data || [];
  }

  async getFacilitySlots(
    societyId: number,
    facilityId: number,
    date: string,
    profileId?: number,
    signal?: AbortSignal
  ): Promise<Slot[]> {
    const res = await api.get(
      `/societies/${societyId}/facilities/${facilityId}/slots`,
      {
        params: { date, profileId },
        signal,
      }
    );
    const slotsData = res.data?.data || res.data || {};
    return Array.isArray(slotsData?.slots)
      ? slotsData.slots
      : Array.isArray(slotsData)
        ? slotsData
        : [];
  }

  async getFairnessProfile(
    societyId: number,
    profileId?: number,
    signal?: AbortSignal
  ): Promise<FairnessProfile | null> {
    if (!profileId) return null;
    const res = await api.get(`/societies/${societyId}/facilities/fairness/${profileId}`, { signal });
    return res.data?.data || res.data || null;
  }

  async getBookings(
    societyId: number,
    params?: { startDate?: string; endDate?: string; status?: string },
    signal?: AbortSignal
  ): Promise<FacilityBooking[]> {
    const res = await api.get(`/societies/${societyId}/facilities/bookings`, {
      params,
      signal,
    });
    return res.data?.data || res.data || [];
  }

  async createBooking(societyId: number, booking: CreateBookingDTO, signal?: AbortSignal): Promise<FacilityBooking> {
    const res = await api.post(`/societies/${societyId}/facilities/bookings`, booking, { signal });
    return res.data?.data || res.data;
  }

  async cancelBooking(societyId: number, bookingId: number, reason?: string, signal?: AbortSignal): Promise<void> {
    await api.post(
      `/societies/${societyId}/facilities/bookings/${bookingId}/cancel`,
      { cancellationReason: reason },
      { signal }
    );
  }

  async approveBooking(societyId: number, bookingId: number, signal?: AbortSignal): Promise<FacilityBooking> {
    const res = await api.post(`/societies/${societyId}/facilities/bookings/${bookingId}/approve`, {}, { signal });
    return res.data?.data || res.data;
  }

  async rejectBooking(societyId: number, bookingId: number, reason?: string, signal?: AbortSignal): Promise<FacilityBooking> {
    const res = await api.post(
      `/societies/${societyId}/facilities/bookings/${bookingId}/reject`,
      { cancellationReason: reason },
      { signal }
    );
    return res.data?.data || res.data;
  }
}
